import type { BrowserQueryString } from '@/scopes/common/infrastructure/query-strings/types'

import type { SearchCriteria } from './types'
import { createBrowserQueryString } from '@/scopes/common/infrastructure/query-strings/browserQueryString'
import {
  createQueryStringSerializer,
  type QueryStringSerializer,
} from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import { getPassThroughParams } from '@/scopes/search/search/domain/getPassThroughParams'
import { searchPassThroughParams } from '@/scopes/search/types'
import defaultFetch from 'isomorphic-unfetch'
import { createFiltersSelectionMapper, type FiltersSelectionMapper } from './filtersSelectionMapper'
import { createSearchResponseMapper, type SearchResponseMapper } from './searchResponseMapper'

interface Dependencies<F> {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
  filterSelectionMapper: FiltersSelectionMapper<F>
  queryStringSerializer: QueryStringSerializer<SearchCriteria<F> & { contextItemId: string }>
  searchResponseMapper: SearchResponseMapper
  contextItemId: string
  browserQuery: BrowserQueryString
}

export function createSearchApiClient<F>(dependencies: Partial<Dependencies<F>> = {}) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    filterSelectionMapper = createFiltersSelectionMapper<F>(),
    queryStringSerializer = createQueryStringSerializer(),
    searchResponseMapper = createSearchResponseMapper(),
    contextItemId = '',
    browserQuery = createBrowserQueryString(),
  } = dependencies as Dependencies<F>

  const fetchOptions: RequestInit = {
    referrerPolicy: 'no-referrer-when-downgrade',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  }

  async function getSearchResults(criteria: SearchCriteria<F>) {
    const url = buildAPIUrl(criteria)

    const response = await fetch(url, fetchOptions)
    const data = await response.json()

    return searchResponseMapper.map(data)
  }

  function buildAPIUrl(criteria: SearchCriteria<F>) {
    const pagination = criteria.pagination ?? {}
    const sorting = getSortingFromCriteria(criteria)
    const filterSelection = getMappedFiltersFromCriteria(criteria)

    const queryParams = new URLSearchParams(browserQuery.get()!)
    const passThroughParams = getPassThroughParams(queryParams, searchPassThroughParams)

    const queryString = queryStringSerializer.serialize({
      contextItemId,
      ...pagination,
      ...sorting,
      ...filterSelection,
      ...passThroughParams,
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/SearchApi/GetSearchResponse?${queryString}`
  }

  function getSortingFromCriteria(criteria: SearchCriteria<F>) {
    return criteria.sorting ? { sort: criteria.sorting.by } : {}
  }

  function getMappedFiltersFromCriteria(criteria: SearchCriteria<F>) {
    return criteria.filterSelection ? filterSelectionMapper.map(criteria.filterSelection) : {}
  }

  async function getAIFilters(userQuery: string) {
    const url = `${env.NEXT_PUBLIC_API_BASE_URL}/SearchApi/GetAIFilters?query=${userQuery}`

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        method: 'POST',
        body: null,
      })

      const data = await response.json()
      return searchResponseMapper.mapAIFilters(data)
    }
    catch (error) {
      console.error('Error fetching AI filters: ', error)
      throw error
    }
  }

  return { getSearchResults, getAIFilters }
}
